import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ModeSelector } from '@/app/components/ModeSelector';
import { IsoBlockEditor } from '@/app/components/IsoBlockEditor';
import { BlockSystemEditor } from '@/app/components/BlockSystemEditor';
import { SystemTableEditor } from '@/app/components/SystemTableEditor';
import { AppMode, SystemData, PlacedBlock, SocketConnection } from '@/app/types/cad';
import { AppModeProvider } from '@/app/providers/AppModeProvider';
import { LanguageProvider } from '@/app/contexts/LanguageContext';
import { TutorialProvider } from '@/app/contexts/TutorialContext';
import { TutorialOverlay, TutorialWelcome } from '@/app/components/tutorial';

// Pre-import Tabs to help Vite with dynamic loading
import '@radix-ui/react-tabs';

// LocalStorage keys
const STORAGE_KEYS = {
  systems: 'cad_systems',
  blocks: 'cad_blocks',
  connections: 'cad_connections',
  projectData: 'cad_projectData',
  currentMode: 'cad_currentMode',
};

// Debounced localStorage save
function useDebouncedLocalStorage(key: string, value: any, delay: number = 500) {
  const timeoutRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      try {
        const serialized = typeof value === 'string' ? value : JSON.stringify(value);
        localStorage.setItem(key, serialized);
      } catch (e) {
        console.error(`Failed to save ${key}:`, e);
      }
    }, delay);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [key, value, delay]);
}

function IsoBlockApp() {
  // Load initial state from localStorage
  const [currentMode, setCurrentMode] = useState<AppMode>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEYS.currentMode);
      return (saved as AppMode) || 'iso-block';
    } catch {
      return 'iso-block';
    }
  });
  
  const [systems, setSystems] = useState<SystemData[]>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEYS.systems);
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });
  
  const [blocks, setBlocks] = useState<PlacedBlock[]>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEYS.blocks);
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });
  
  const [connections, setConnections] = useState<SocketConnection[]>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEYS.connections);
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });
  
  const [projectData, setProjectData] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEYS.projectData);
      return saved ? JSON.parse(saved) : {
        name: 'Mein Projekt',
        customAttributes: {},
      };
    } catch {
      return {
        name: 'Mein Projekt',
        customAttributes: {},
      };
    }
  });

  // Use debounced localStorage saves for performance
  useDebouncedLocalStorage(STORAGE_KEYS.currentMode, currentMode, 100);
  useDebouncedLocalStorage(STORAGE_KEYS.systems, systems, 500);
  useDebouncedLocalStorage(STORAGE_KEYS.blocks, blocks, 500);
  useDebouncedLocalStorage(STORAGE_KEYS.connections, connections, 500);
  useDebouncedLocalStorage(STORAGE_KEYS.projectData, projectData, 500);

  const handleUpdateSystem = useCallback((systemId: string, updates: Partial<SystemData>) => {
    setSystems(prev => prev.map(sys => 
      sys.id === systemId ? { ...sys, ...updates } : sys
    ));
  }, []);

  const handleUpdateProjectData = useCallback((updates: any) => {
    setProjectData(prev => ({ ...prev, ...updates }));
  }, []);

  return (
    <AppModeProvider mode={currentMode}>
      <div className="flex flex-col h-screen overflow-hidden" style={{ backgroundColor: 'var(--md-background)' }}>
        {/* Mode Selector Tabs */}
        <ModeSelector currentMode={currentMode} onModeChange={setCurrentMode} />

        {/* Render current editor based on mode */}
        {currentMode === 'iso-block' && <IsoBlockEditor />}
        {currentMode === 'block-system' && (
          <BlockSystemEditor 
            onSystemsChange={setSystems}
            onBlocksChange={setBlocks}
            onConnectionsChange={setConnections}
          />
        )}
        {currentMode === 'system-table' && (
          <SystemTableEditor
            systems={systems}
            onUpdateSystem={handleUpdateSystem}
            projectData={projectData}
            onUpdateProjectData={handleUpdateProjectData}
            blocks={blocks}
            connections={connections}
            onBlocksChange={setBlocks}
            onConnectionsChange={setConnections}
          />
        )}
      </div>
    </AppModeProvider>
  );
}

// Main App wrapper with providers
function App() {
  const [showWelcomeDialog, setShowWelcomeDialog] = useState(false);

  // Check if should show welcome on first start
  useEffect(() => {
    try {
      const tutorialState = localStorage.getItem('isocad_tutorial_state');
      if (!tutorialState) {
        // First time user
        setShowWelcomeDialog(true);
      } else {
        const state = JSON.parse(tutorialState);
        if (!state.hasSeenWelcome && !state.neverShowAgain) {
          setShowWelcomeDialog(true);
        }
      }
    } catch (e) {
      console.error('Failed to check tutorial state:', e);
    }
  }, []);

  return (
    <LanguageProvider>
      <TutorialProvider>
        <IsoBlockApp />
        <TutorialOverlay />
        {showWelcomeDialog && <TutorialWelcome onClose={() => setShowWelcomeDialog(false)} />}
      </TutorialProvider>
    </LanguageProvider>
  );
}

export default App;